import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import type { SupportedLang } from '../../lib/i18n'

const LANGS: { code: SupportedLang; flag: string; label: string }[] = [
  { code: 'es', flag: '/flags/es.png', label: 'Español' },
  { code: 'en', flag: '/flags/en.png', label: 'English' },
]

const LangSwitcher = ({ className }: { className?: string }) => {
  const { i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = (i18n.language?.slice(0, 2) as SupportedLang) || 'es'
  const currentLang = LANGS.find(l => l.code === current) || LANGS[0]

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1 px-1.5 py-1 rounded-full',
          'bg-gray-100 hover:bg-gray-200 transition-colors border border-gray-200/80'
        )}
        aria-label="Change language"
      >
        <img
          src={currentLang.flag}
          alt=""
          className="w-6 h-6 rounded-full object-cover ring-1 ring-gray-200/60"
        />
        <svg className={cn('w-3 h-3 text-gray-500 transition-transform', open && 'rotate-180')} viewBox="0 0 8 8">
          <path d="M1 3L4 6L7 3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1.5 py-1 min-w-[120px] bg-white rounded-2xl shadow-lg border border-gray-200/80 overflow-hidden z-50">
          {LANGS.map(({ code, flag, label }) => (
            <button
              key={code}
              onClick={() => {
                i18n.changeLanguage(code)
                setOpen(false)
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                current === code ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50 text-gray-700'
              )}
            >
              <img
                src={flag}
                alt=""
                className="w-6 h-6 rounded-full object-cover ring-1 ring-gray-200/60"
              />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LangSwitcher
